<template>
    <div class="provider-communications-section">
        <div v-if="!hasViewPermission" class="text-muted small">
            You do not have permission to view provider communications.
        </div>
        <template v-else>
            <div class="d-flex align-items-center mb-2">
                <div class="flex-fill">
                    <h4 class="card-subtitle d-inline-block mb-0">Provider Communications</h4>
                    <b-badge v-if="totalUnread > 0" variant="warning" class="ml-2">
                        {{ totalUnread }} unread
                    </b-badge>
                </div>
                <div>
                    <b-form-checkbox v-model="includeResolved" inline class="mr-2" @change="loadThreads">
                        Include resolved
                    </b-form-checkbox>
                    <b-button v-if="hasSendPermission" size="sm" variant="primary" @click="openInitiateModal">
                        <i class="fa fa-comment-dots mr-1"></i>
                        New Message
                    </b-button>
                </div>
            </div>

            <b-spinner v-if="loading" small class="mr-2" />
            <div v-else-if="threads.length === 0" class="text-muted small">
                No provider communications for this patient.
            </div>
            <b-table v-else :items="threads" :fields="fields" small responsive @row-clicked="openThread"
                tbody-tr-class="cursor-pointer">
                <template #cell(prescriber)="row">
                    <span>{{ prescriberName(row.item) }}</span>
                    <small class="text-muted d-block">NPI {{ row.item.prescriberNPI }}</small>
                </template>
                <template #cell(lastMessage)="row">
                    <div class="text-truncate" style="max-width: 30em;">
                        <i :class="row.item.lastMessageInbound ? 'fa fa-arrow-down text-success' : 'fa fa-arrow-up text-secondary'"
                            class="mr-1"></i>
                        {{ row.item.lastMessageBody || "(no messages)" }}
                    </div>
                    <small class="text-muted">{{ formatDate(row.item.lastMessageDate) }}</small>
                </template>
                <template #cell(status)="row">
                    <b-badge v-if="row.item.isResolved" variant="success">Resolved</b-badge>
                    <b-badge v-else-if="row.item.unreadCount > 0" variant="warning">
                        {{ row.item.unreadCount }} unread
                    </b-badge>
                    <b-badge v-else variant="primary">Open</b-badge>
                </template>
            </b-table>
        </template>

        <provider-communication-modal v-if="modalThread || modalInitiate" :thread="modalThread"
            :initiate-context="modalInitiate" :patient-id="patientId" @close="closeModal" @saved="handleSaved" />
    </div>
</template>

<script lang="ts">
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import {
    providerCommunicationsService,
    providerCommunicationsPermissions,
    ProviderCommunicationThread,
} from "@/services/providerCommunicationsService";
import ProviderCommunicationModal from "./ProviderCommunicationModal.vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import moment from "moment";

@Component({ components: { ProviderCommunicationModal } })
export default class ProviderCommunicationsSection extends Vue {
    @Prop({ type: Number, required: true }) patientId!: number;

    threads: ProviderCommunicationThread[] = [];
    loading = false;
    includeResolved = false;
    modalThread: ProviderCommunicationThread | null = null;
    modalInitiate: { patientId: number } | null = null;

    fields = [
        { key: "prescriber", label: "Prescriber" },
        { key: "lastMessage", label: "Last Message" },
        { key: "status", label: "Status", thStyle: { width: "8em" } },
    ];

    get hasViewPermission(): boolean {
        return (this as any).$permissions.some(
            (p: { permissionCodeName: string }) => p.permissionCodeName === providerCommunicationsPermissions.view
        );
    }

    get hasSendPermission(): boolean {
        return (this as any).$permissions.some(
            (p: { permissionCodeName: string }) => p.permissionCodeName === providerCommunicationsPermissions.send
        );
    }

    get totalUnread(): number {
        return this.threads.reduce((sum, t) => sum + (t.unreadCount || 0), 0);
    }

    @Watch("patientId", { immediate: true })
    onPatientChanged() {
        if (this.hasViewPermission) {
            this.loadThreads();
        }
    }

    async loadThreads() {
        if (!this.hasViewPermission) return;
        this.loading = true;
        try {
            this.threads = await providerCommunicationsService.listThreads(this.patientId, this.includeResolved);
        } catch (err) {
            (this as any).$notification(NotificationOptions.error(err));
        } finally {
            this.loading = false;
        }
    }

    openThread(thread: ProviderCommunicationThread) {
        this.modalThread = thread;
        this.modalInitiate = null;
    }

    openInitiateModal() {
        this.modalInitiate = { patientId: this.patientId };
        this.modalThread = null;
    }

    closeModal() {
        this.modalThread = null;
        this.modalInitiate = null;
    }

    handleSaved() {
        this.loadThreads();
    }

    prescriberName(t: ProviderCommunicationThread): string {
        const parts = [t.prescriberFirstName, t.prescriberLastName].filter(Boolean);
        return parts.length ? parts.join(" ") : "Unknown prescriber";
    }

    formatDate(value?: string | null): string {
        return value ? moment.utc(value).local().format("M/D/YYYY h:mm a") : "";
    }
}
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}
</style>
